/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { propsUtils } from '@ba-ui-toolkit/utils';
import {
	ExpandableTile as CarbonExpandableTile,
	TileAboveTheFoldContent,
	TileBelowTheFoldContent,
} from 'carbon';
import { omit } from 'lodash';

const { BaPropTypes } = propsUtils;

export default class ExpandableTile extends Component {
	static propTypes = {
		/** Data passed to the above the fold content
		 *  Legacy tooltip prop will map to TileAboveTheFoldContent
		 */
		label: PropTypes.node,
		/**
		 * Carbon The child nodes.
		 * Or legacy tooltip child nodes will map to TileBelowTheFoldContent
		 * Data passed to the below the fold content
		 */
		children: PropTypes.node,

		/**
		 * The CSS class names.
		 */
		className: PropTypes.string,

		/**
		 * Legacy toolkit prop will map to expanded
		 * Specifies whether the tile is initially expanded or not */
		initialExpanded: PropTypes.bool,
		/**
		 * `true` if the tile is expanded.
		 */
		expanded: PropTypes.bool,

		/**
		 * optional handler to decide whether to ignore a click. returns false if click should be ignored
		 */
		onBeforeClick: PropTypes.func,

		/**
		 * The `tabindex` attribute.
		 */
		tabIndex: PropTypes.number,

		/**
		 * The description of the "collapsed" icon that can be read by screen readers.
		 */
		tileCollapsedIconText: PropTypes.string,

		/**
		 * The description of the "expanded" icon that can be read by screen readers.
		 */
		tileExpandedIconText: PropTypes.string,

		/**
		 * An ID that can be provided to aria-labelledby
		 */
		id: PropTypes.string,

		/**
		 * `true` to use the light version. For use on $ui-01 backgrounds only.
		 * Don't use this to make tile background color same as container background color.
		 */
		light: PropTypes.bool,

		/** Legacy toolkit prop
		 * Specifies the maximum width of the ExpandableTile
		 */
		maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

		/** Legacty toolkit prop, will pass to TileAboveTheFoldContent
		 * Styles for the TileAboveTheFoldContent
		 */
		labelStyle: PropTypes.object,

		/** Legacty toolkit prop, will pass to CarbonExpandableTile
		 * Styles for the CarbonExpandableTile
		 */
		style: PropTypes.object,

		/**
		 * Legacy toolkit prop will map to handleClick
		 * Callback function when the item is toggled
		 */
		onChange: PropTypes.func,

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		pinMode: BaPropTypes.deprecatedByCarbon(CarbonExpandableTile),
	};

	static defaultProps = {
		maxWidth: 'max-content',
	};

	render() {
		const {
			maxWidth,
			label,
			children,
			labelStyle,
			initialExpanded,
			expanded,
			onChange,
			...others
		} = this.props;
		const mappedProps = {
			expanded: expanded != null ? expanded : initialExpanded,
			handleClick: onChange,
		};
		const passThroughProps = omit(others, 'pinMode');

		return (
			<CarbonExpandableTile maxWidth={maxWidth} {...mappedProps} {...passThroughProps}>
				{label != null ? (
					<>
						<TileAboveTheFoldContent>
							<div style={labelStyle}>{label}</div>
						</TileAboveTheFoldContent>
						<TileBelowTheFoldContent>{children}</TileBelowTheFoldContent>
					</>
				) : (
					children
				)}
			</CarbonExpandableTile>
		);
	}
}
